import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import BrochureLeadForm from './BrochureLeadForm';
import ProductEnquiryForm from './ProductEnquiryForm';

const CatalogSection = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [isBrochureFormOpen, setIsBrochureFormOpen] = useState(false);
  const [isEnquiryFormOpen, setIsEnquiryFormOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState([
    { id: 'all', name: 'All Products', icon: '🎁' }
  ]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const q = query(collection(db, 'categories'), orderBy('createdAt', 'asc'));
      const querySnapshot = await getDocs(q);
      const categoriesData = [
        { id: 'all', name: 'All Products', icon: '🎁' }
      ];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        categoriesData.push({
          id: doc.id,
          name: data.name,
          icon: data.icon || '📦'
        });
      });
      
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Keep default categories as fallback
      setCategories([
        { id: 'all', name: 'All Products', icon: '🎁' },
        { id: 'corporate', name: 'Corporate Gifts', icon: '💼' },
        { id: 'festivals', name: 'Festival Hampers', icon: '🪔' },
        { id: 'return', name: 'Return Gifts', icon: '🎪' },
        { id: 'personalized', name: 'Personalized Gifts', icon: '✨' }
      ]);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      
      // First fetch all categories to create a lookup map
      const categoriesQuery = query(collection(db, 'categories'), orderBy('createdAt', 'asc'));
      const categoriesSnapshot = await getDocs(categoriesQuery);
      const categoryMap = {
        'corporate': 'Corporate Gifts',
        'festivals': 'Festival Hampers', 
        'return': 'Return Gifts',
        'personalized': 'Personalized Gifts'
      };
      
      categoriesSnapshot.forEach((doc) => {
        const data = doc.data();
        categoryMap[doc.id] = data.name;
      });
      
      // Then fetch products and resolve category names
      const q = query(collection(db, 'products'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const productsData = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        productsData.push({
          id: doc.id,
          name: data.name,
          price: data.price,
          image: data.imageUrl || '/gifts/IMG-20250903-WA0054.jpg', // fallback image
          description: data.description,
          category: data.category, // Keep original category ID for filtering
          categoryName: categoryMap[data.category] || data.category // Add resolved category name
        });
      });
      
      setAllProducts(productsData);
    } catch (error) {
      console.error('Error fetching products:', error);
      // Keep empty array as fallback
      setAllProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEnquireNow = (product) => {
    setSelectedProduct(product);
    setIsEnquiryFormOpen(true);
  };

  const closeEnquiryForm = () => {
    setIsEnquiryFormOpen(false);
    setSelectedProduct(null);
  };

  // Filter products based on category
  const filteredProducts = activeCategory === 'all' 
    ? allProducts 
    : allProducts.filter(product => product.category === activeCategory);

  return (
    <section id="catalog" className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-burlap-50 via-cream-50 to-burlap-100 burlap-texture px-4 sm:px-6">
      <div className="container mx-auto">
        <div className="text-center mb-16 sm:mb-20">
          <div className="inline-block mb-4">
            <span className="text-xs sm:text-sm font-bold text-accent-600 uppercase tracking-widest bg-accent-100 px-3 sm:px-4 py-2 rounded-full">
              Product Catalog
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-display font-bold text-burlap-800 mb-4 sm:mb-6">
            Our <span className="gradient-gold">Gift Collections</span>
          </h2>
          <p className="text-lg sm:text-xl text-burlap-600 max-w-4xl mx-auto leading-relaxed mb-8">
            Discover our extensive range of premium gifts perfect for corporate events, 
            festivals, celebrations, and personalized occasions.
          </p>
          
          {/* Download Brochure Button */}
          <button 
            onClick={() => setIsBrochureFormOpen(true)}
            className="inline-flex items-center space-x-3 bg-gradient-to-r from-primary-500 to-accent-500 hover:from-primary-600 hover:to-accent-600 text-white font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
          >
            <span>📥</span>
            <span>Download Complete Brochure</span>
          </button>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-12 sm:mb-16">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`group px-4 sm:px-6 lg:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-semibold transition-all duration-500 transform hover:scale-105 border-2 text-sm sm:text-base ${
                activeCategory === category.id
                  ? 'bg-gradient-to-r from-primary-500 to-accent-500 text-white shadow-2xl border-transparent scale-105'
                  : 'bg-white/80 text-burlap-700 hover:bg-white border-burlap-200 shadow-lg hover:shadow-xl'
              }`}
            >
              <div className="flex items-center space-x-2 sm:space-x-3">
                <span className="text-lg sm:text-2xl">{category.icon}</span>
                <span className="font-bold text-xs sm:text-sm uppercase tracking-wide">{category.name}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8">
            {[...Array(8)].map((_, index) => (
              <div key={index} className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg border border-cream-200 animate-pulse">
                <div className="w-full h-48 sm:h-56 bg-burlap-200 rounded-lg sm:rounded-xl mb-4"></div>
                <div className="h-6 bg-burlap-200 rounded mb-2"></div>
                <div className="h-4 bg-burlap-100 rounded mb-4"></div>
                <div className="flex justify-between items-center">
                  <div className="h-6 bg-burlap-200 rounded w-24"></div>
                  <div className="h-4 bg-burlap-100 rounded w-20"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8">
          {filteredProducts.map((product) => (
            <div 
              key={product.id}
              className="group bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:scale-105 border border-cream-200 hover:border-primary-300"
            >
              {/* Product Image */}
              <div className="w-full h-48 sm:h-56 bg-gradient-to-br from-burlap-100 to-burlap-200 rounded-lg sm:rounded-xl mb-4 overflow-hidden border-2 border-burlap-200 group-hover:border-primary-300 transition-all duration-300">
                <img 
                  src={product.image} 
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="w-full h-full bg-gradient-to-br from-burlap-100 to-burlap-200 rounded-lg flex items-center justify-center hidden">
                  <span className="text-4xl text-burlap-400">🎁</span>
                </div>
              </div>
              
              {/* Product Info */}
              <h5 className="text-lg sm:text-xl font-bold text-burlap-800 mb-2 font-display group-hover:text-primary-600 transition-colors">
                {product.name}
              </h5>
              <p className="text-burlap-600 text-sm mb-4 leading-relaxed">
                {product.description}
              </p>
              
              {/* Price and Action */}
              <div className="flex items-center justify-between">
                <span className="text-lg sm:text-xl font-bold text-primary-600">
                  <span className="text-base sm:text-lg mr-1">From ₹</span>
                  {product.price}
                </span>
                <button 
                  onClick={() => handleEnquireNow(product)}
                  className="text-sm text-burlap-600 hover:text-primary-600 font-medium transition-colors duration-300 group-hover:translate-x-1 transform"
                >
                  Enquire Now →
                </button>
              </div>
            </div>
          ))}
          </div>
        )}

        {/* Service Highlights */}
        <div className="mt-16 sm:mt-20 grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
          {[
            { icon: '🎯', title: 'Bulk Orders', subtitle: 'Volume Discounts', desc: 'Special pricing for large quantities' },
            { icon: '📦', title: 'Gift Wrapping', subtitle: 'Premium Presentation', desc: 'Beautiful packaging included' },
            { icon: '🚚', title: 'Pan India Delivery', subtitle: 'Timely Shipping', desc: 'Reliable delivery across India' },
            { icon: '💬', title: 'Expert Support', subtitle: '24/7 Assistance', desc: 'Dedicated gift consultants' }
          ].map((service, index) => (
            <div 
              key={index}
              className="text-center bg-white/60 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 border border-cream-200"
            >
              <div className="text-2xl sm:text-3xl mb-3 sm:mb-4">{service.icon}</div>
              <h4 className="text-sm sm:text-lg font-bold text-burlap-800 mb-1">{service.title}</h4>
              <p className="text-xs sm:text-sm font-medium text-primary-600 mb-1 sm:mb-2">{service.subtitle}</p>
              <p className="text-xs text-burlap-600 hidden sm:block">{service.desc}</p>
            </div>
          ))}
        </div>
      </div>
      
      {/* Brochure Lead Form Modal */}
      <BrochureLeadForm 
        isOpen={isBrochureFormOpen} 
        onClose={() => setIsBrochureFormOpen(false)} 
      />
      
      {/* Product Enquiry Form Modal */}
      <ProductEnquiryForm 
        isOpen={isEnquiryFormOpen} 
        onClose={closeEnquiryForm} 
        product={selectedProduct}
      />
    </section>
  );
};

export default CatalogSection;
